import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import AuthCard from '@/components/AuthCard'
import SocialSignIn from '@/components/SocialSignIn'
import { Button } from '@/components/ui/button'
import {
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { useResendCooldown } from '@/hooks/use-resend-cooldown'
import { capture } from '@/lib/analytics/posthog'
import { authClient, signUp } from '@/lib/auth-client'
import { getEnabledSocialProviders } from '@/lib/auth-social'
import { pageLinks, pageMeta } from '@/lib/seo'

export const Route = createFileRoute('/signup')({
  head: () => ({
    // noindex, same reasoning as /login.
    meta: pageMeta({
      title: 'Sign up · Masklabs',
      description:
        'Create a Masklabs account and get US mobile proxies in minutes. Monthly plans from $30, refill data anytime, no minimum commit.',
      path: '/signup',
      noindex: true,
    }),
    links: pageLinks('/signup'),
  }),
  // Marketing "Buy now" buttons pass the chosen plan so the account lands on
  // that plan's checkout after verification.
  validateSearch: (
    search: Record<string, unknown>,
  ): { plan?: string; trial?: boolean } => ({
    plan:
      typeof search.plan === 'string' && /^[a-z0-9-]{1,32}$/.test(search.plan)
        ? search.plan
        : undefined,
    // The free-trial link: the account lands on the trial checkout instead of
    // the plan's paid one.
    trial: search.trial === true || search.trial === 'true' ? true : undefined,
  }),
  loader: async () => ({
    socialProviders: await getEnabledSocialProviders(),
  }),
  component: SignupPage,
})

/**
 * Where the verification link (and a normal sign-in) should land: the
 * dashboard, or straight into checkout for the plan picked on the
 * marketing site.
 */
function postSignupPath(plan?: string, trial?: boolean): string {
  if (!plan) return '/dashboard'
  const search = new URLSearchParams({ subscribe: plan })
  if (trial) search.set('trial', 'true')
  return `/billing?${search.toString()}`
}

function callbackUrl(plan?: string, trial?: boolean): string {
  return `${window.location.origin}${postSignupPath(plan, trial)}`
}

type ResendState = 'idle' | 'sending' | 'sent' | 'rate-limited' | 'error'

function SignupPage() {
  const { plan, trial } = Route.useSearch()
  const { socialProviders } = Route.useLoaderData()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)
  // Set once sign-up succeeds without an active session — i.e. email
  // verification is required. Switches the card to a "check your email" view.
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null)
  const [resendState, setResendState] = useState<ResendState>('idle')
  const { remaining, start } = useResendCooldown()

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setPending(true)
    const { data, error: signUpError } = await signUp.email({
      name,
      email,
      password,
      callbackURL: callbackUrl(plan, trial),
    })
    setPending(false)
    if (signUpError) {
      setError(signUpError.message ?? 'Sign up failed.')
      return
    }
    if (data?.token) {
      // Verification isn't required (or already satisfied) — a session was
      // created, so go straight in.
      capture('signup_submitted', {
        method: 'email',
        requires_verification: false,
        plan,
        trial,
      })
      window.location.href = postSignupPath(plan, trial)
      return
    }
    // No session yet: the account needs email verification first. We can't
    // tell from here whether the email actually made it out (sending never
    // throws), so the copy below stays generic and offers a resend instead
    // of promising delivery.
    capture('signup_submitted', {
      method: 'email',
      requires_verification: true,
      plan,
      trial,
    })
    setSubmittedEmail(email)
  }

  async function onResend() {
    if (!submittedEmail) return
    setResendState('sending')
    const { error: resendError } = await authClient.sendVerificationEmail({
      email: submittedEmail,
      callbackURL: callbackUrl(plan, trial),
    })
    if (!resendError) {
      setResendState('sent')
      start()
      return
    }
    setResendState(resendError.status === 429 ? 'rate-limited' : 'error')
  }

  if (submittedEmail) {
    return (
      <AuthCard>
        <CardHeader>
          <CardTitle className="text-2xl">Check your email</CardTitle>
          <CardDescription>
            We sent a verification link to{' '}
            <span className="font-medium text-foreground">
              {submittedEmail}
            </span>
            . Click it to activate your account.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Didn't get it? Check your spam folder, or request a new link below.
          </p>
          {resendState === 'sent' ? (
            <p className="mt-3 text-sm text-foreground">
              Verification email sent. Check your inbox.
            </p>
          ) : null}
          {resendState === 'rate-limited' ? (
            <p className="mt-3 text-sm text-destructive">
              You've requested a few links already. Please wait a bit before
              trying again.
            </p>
          ) : null}
          {resendState === 'error' ? (
            <p className="mt-3 text-sm text-destructive">
              Couldn't resend the verification email. Please try again in a
              moment.
            </p>
          ) : null}
        </CardContent>
        <CardFooter className="flex-col items-stretch gap-3">
          <Button
            type="button"
            variant="outline"
            className="w-full"
            disabled={resendState === 'sending' || remaining > 0}
            onClick={onResend}
          >
            {resendState === 'sending'
              ? 'Sending…'
              : remaining > 0
                ? `Resend in ${remaining}s`
                : 'Resend verification email'}
          </Button>
          <p className="text-center text-sm text-muted-foreground">
            Already verified?{' '}
            <Link
              to="/login"
              className="text-foreground underline-offset-4 hover:underline"
            >
              Sign in
            </Link>
          </p>
        </CardFooter>
      </AuthCard>
    )
  }

  return (
    <AuthCard>
      <CardHeader>
        <CardTitle className="text-2xl">Create account</CardTitle>
        <CardDescription>Start using Masklabs proxies.</CardDescription>
      </CardHeader>
      <form onSubmit={onSubmit} className="flex flex-col gap-6">
        <CardContent>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="name">Name</FieldLabel>
              <Input
                id="name"
                autoComplete="name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="email">Email</FieldLabel>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="password">Password</FieldLabel>
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>
            {error ? <p className="text-sm text-destructive">{error}</p> : null}
          </FieldGroup>
        </CardContent>
        <CardFooter className="flex-col items-stretch gap-3">
          <Button type="submit" className="w-full" disabled={pending}>
            {pending ? 'Creating account…' : 'Create account'}
          </Button>
          <SocialSignIn
            providers={socialProviders}
            callbackPath={postSignupPath(plan, trial)}
          />
          <p className="text-center text-sm text-muted-foreground">
            Already have an account?{' '}
            <Link
              to="/login"
              className="text-foreground underline-offset-4 hover:underline"
            >
              Sign in
            </Link>
          </p>
        </CardFooter>
      </form>
    </AuthCard>
  )
}
